import React, { useCallback, useRef, useState } from "react";
import { Box, Typography, Collapse, CircularProgress, Tooltip } from "@mui/material";
import { get_student_questions_api, get_student_subtopics_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, font, success, warning,
  surfaceRaised, easing,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { humaniseTopic } from "./recordTheme";

/**
 * The course, one card per subject.
 *
 * WHY A CARD AND NOT A ROW
 * A subject is four different measurements — how much they have answered, how
 * much of the paper they have actually been near, how well they did, and how
 * long ago. In a table those become four columns of the same weight, and the
 * one that matters most for a real student (coverage) is the one a column
 * cannot show at all. A card gives each subject a headline, a stat line and a
 * bar, and lets a subject with nothing in it collapse to a single line.
 *
 * COVERAGE AND SCORE ARE DIFFERENT QUESTIONS
 * A percentage says how well they did on whatever they happened to see. It
 * says nothing about how much of the paper they have met. Both real students
 * in this database show the gap plainly: Glen has 112 answers in
 * Communications and has touched 1 of its 11 topics, because nearly all of it
 * is whole-subject practice. "63% over 67 answers" and "one corner of the
 * syllabus" are the same student, and only one of those was ever on screen.
 */

/** Small, quiet, and unmistakably a button. */
const actionSx = (primary) => ({
  fontFamily: font, fontSize: "11.5px", fontWeight: 700,
  px: 1.1, py: 0.4, borderRadius: "999px", cursor: "pointer", userSelect: "none",
  transition: `all 0.12s ${easing}`,
  ...(primary
    ? { color: "#fff", backgroundColor: accent, "&:hover": { backgroundColor: "#a81828" } }
    : { color: textPrimary, backgroundColor: "#fff", border: `1px solid ${border}`, "&:hover": { borderColor: accent, color: accent } }),
});

const ago = (d) => {
  if (!d) return null;
  const days = Math.round((Date.now() - new Date(d)) / 864e5);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
};

/**
 * The readiness rating, in five states. "Cannot say yet" is one of them, and
 * "was there" is another — a pass mark with no date on it told an instructor a
 * student was above 75% on questions they last answered seven months ago.
 */
function ratingOf(s) {
  if (!s.done) return { label: "Not started", color: textMuted, bg: "transparent", border };
  if (s.ready === null) return { label: "Too early to say", color: textSecondary, bg: surfaceRaised, border };
  if (s.ready && s.faded) return { label: `Was above ${s.passMark}%`, color: textSecondary, bg: surfaceRaised, border };
  if (s.ready) return { label: `Above ${s.passMark}%`, color: success, bg: "rgba(22,163,74,0.09)", border: "rgba(22,163,74,0.25)" };
  return { label: `Below ${s.passMark}%`, color: warning, bg: "rgba(217,119,6,0.09)", border: "rgba(217,119,6,0.25)" };
}

function Rating({ subject }) {
  const r = ratingOf(subject);
  return (
    <Typography sx={{
      fontFamily: font, fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap",
      px: 0.9, py: 0.3, borderRadius: "5px",
      color: r.color, backgroundColor: r.bg, border: `1px solid ${r.border}`,
    }}>
      {r.label}
    </Typography>
  );
}

/**
 * One segment per topic in the paper — the whole syllabus at a glance.
 *
 * A single filled bar says "3 of 27" twice: once as a number and once as a
 * stub of colour, and leaves the card looking empty. A segment per topic uses
 * the same space to say which ones, how they went, and how much is still
 * untouched — and it is the same shape as the list that opens underneath, so
 * the card and its contents are obviously the same object.
 */
function CoverageBar({ covered, total, mixedOnly, topics = [], passMark = 75 }) {
  if (!total) return null;
  // The list is already ordered worked-first; the strip follows the course's
  // order instead, so a paper looks the same on every student's record.
  const segments = topics.filter((t) => t.topic !== "all");
  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
        {/* Said in words: "10 of 27" on its own read as a score. Each bar
            segment is one topic — green 75%+, orange below, grey not yet. */}
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
          Covered {covered} of the {total} topics in this paper
          <Box component="span" sx={{ color: textMuted }}> · most of each topic done</Box>
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
          <Box component="span" sx={{ color: "#16a34a" }}>■</Box> 75%+ &nbsp;
          <Box component="span" sx={{ color: "#d97706" }}>■</Box> below &nbsp;
          <Box component="span" sx={{ color: "#F3DFC2" }}>■</Box> started &nbsp;
          <Box component="span" sx={{ color: "#d6d1cb" }}>■</Box> not yet
        </Typography>
      </Box>

      {segments.length ? (
        <Box sx={{ display: "flex", gap: "2px", height: 7 }}>
          {segments.map((t) => (
            <Tooltip
              key={t.topic}
              arrow
              title={`${humaniseTopic(t.topic)} — ${t.microTotal ? `${t.subCovered || 0} of ${t.microTotal} subtopics covered` : ""}${t.done ? ` · ${t.right}/${t.done} right` : ""}${!t.done && !t.subCovered ? " — not started" : ""}`}
            >
              <Box sx={{
                flex: 1, minWidth: 3, borderRadius: "2px",
                // Covered = most of its subtopics done (server: `completed`).
                // Started = something done, not most of it yet: a pale tint.
                backgroundColor: t.completed === false
                  ? (t.started ? "#F3DFC2" : surfaceRaised)
                  : !t.done ? (t.completed ? success : surfaceRaised)
                  : t.percent >= passMark ? success : warning,
                opacity: 0.9,
                transition: `opacity 0.12s ${easing}`,
                "&:hover": { opacity: 1 },
              }} />
            </Tooltip>
          ))}
        </Box>
      ) : (
        <Box sx={{ height: 7, borderRadius: 3, backgroundColor: surfaceRaised }} />
      )}
      {/* The honest caveat, and the one an instructor can act on: volume
          without breadth. You cannot claim topic coverage from a mixed
          session, so it is not counted as any. */}
      {mixedOnly && (
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mt: 0.4, lineHeight: 1.45 }}>
          All of it was mixed practice — no single topic covered yet.
        </Typography>
      )}
    </Box>
  );
}

/** The questions themselves. Loaded on demand, cached for the session. */
function Questions({ studentId, subject, topic, wrongCount, seed, cache }) {
  const [wrongOnly, setWrongOnly] = useState(true);
  const key = `${subject}::${topic || ""}::${wrongOnly ? "wrong" : "all"}`;
  const [state, setState] = useState(() => cache.current.get(key) || { loading: true, rows: [] });

  const load = useCallback(() => {
    const hit = cache.current.get(key);
    if (hit) { setState(hit); return; }
    setState({ loading: true, rows: [] });
    get_student_questions_api(studentId, { subject, topic: topic || undefined, wrongOnly, limit: 6 })
      .then((r) => {
        const next = { loading: false, rows: r?.questions || [] };
        cache.current.set(key, next);
        setState(next);
      })
      .catch(() => setState({ loading: false, rows: [] }));
  }, [studentId, subject, topic, wrongOnly, key, cache]);

  React.useEffect(() => { load(); }, [load]);

  // Question text is stored on roughly a quarter of attempts — the rest are
  // generated live. Saying so once stops a panel of bare answer pairs reading
  // as a page that failed to load.
  const anyText = state.rows.some((q) => q.questionText);
  // The sample from the coverage payload paints the first row instantly, so
  // the panel is never an empty box with a spinner in it.
  const shown = state.loading && seed ? [{ ...seed, isCorrect: false }] : state.rows;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: textSecondary }}>
          {wrongOnly ? `${wrongCount || shown.length} wrong, newest first` : "Everything, newest first"}
        </Typography>
        <Typography
          onClick={() => setWrongOnly((v) => !v)}
          sx={{ fontFamily: font, fontSize: "11px", color: accent, cursor: "pointer", flexShrink: 0, "&:hover": { textDecoration: "underline" } }}
        >
          {wrongOnly ? "show all" : "only the wrong ones"}
        </Typography>
      </Box>

      {state.loading && !seed && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          <CircularProgress size={12} sx={{ color: accent }} />
          <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>Loading</Typography>
        </Box>
      )}

      {!state.loading && !state.rows.length && (
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>
          {wrongOnly ? "Nothing wrong here recently." : "Nothing recorded here."}
        </Typography>
      )}

      {!state.loading && state.rows.length > 0 && !anyText && (
        <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mb: 0.5 }}>
          Question text isn't stored for most attempts — the answers are.
        </Typography>
      )}

      {shown.map((q, i) => (
        <Box key={i} sx={{ py: 0.65, borderTop: i === 0 ? "none" : `1px solid ${border}` }}>
          {q.questionText && (
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textPrimary, lineHeight: 1.5, mb: 0.25 }}>
              {q.questionText}
            </Typography>
          )}
          <Typography sx={{ fontFamily: font, fontSize: "12px", lineHeight: 1.6 }}>
            <Box component="span" sx={{ color: q.isCorrect ? success : warning, fontWeight: 600 }}>
              {q.isCorrect ? "answered" : "chose"}
            </Box>
            <Box component="span" sx={{ color: textSecondary }}> {q.chose || "—"}</Box>
            {!q.isCorrect && (
              <>
                <Box component="span" sx={{ color: textMuted }}> · </Box>
                <Box component="span" sx={{ color: success, fontWeight: 600 }}>right</Box>
                <Box component="span" sx={{ color: textSecondary }}> {q.correct || "—"}</Box>
              </>
            )}
          </Typography>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.15 }}>
            {ago(q.answeredAt)}{q.seconds ? ` · ${q.seconds}s` : ""}{q.viewedExplanation ? " · read the explanation" : ""}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The subtopics the course puts under one topic.
 *
 * HONEST ABOUT WHAT PRACTICE RECORDS
 * `microTopic` is written on about a third of attempts and on some students'
 * work never at all — Glen has 63 answers on aerodrome operations and not one
 * of them carries a subtopic. A breakdown that renders that as "nothing
 * covered" is a lie about the student, so it is stated as what it is: a gap in
 * what was recorded.
 */
function Subtopics({ studentId, subject, topic, cache }) {
  const key = `sub::${subject}::${topic}`;
  const [state, setState] = useState(() => cache.current.get(key) || { loading: true, data: null });

  React.useEffect(() => {
    const hit = cache.current.get(key);
    if (hit) { setState(hit); return; }
    let dead = false;
    get_student_subtopics_api(studentId, { subject, topic })
      .then((r) => {
        const next = { loading: false, data: r?.code === 200 ? r : null };
        cache.current.set(key, next);
        if (!dead) setState(next);
      })
      .catch(() => { if (!dead) setState({ loading: false, data: null }); });
    return () => { dead = true; };
  }, [studentId, subject, topic, key, cache]);

  if (state.loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
        <CircularProgress size={12} sx={{ color: accent }} />
        <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted }}>Loading subtopics</Typography>
      </Box>
    );
  }

  const d = state.data;
  const list = d?.subtopics || [];

  if (!list.length) {
    return (
      <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, py: 0.75, lineHeight: 1.5 }}>
        {d?.unfiled
          ? `No subtopic was recorded on any of these ${d.unfiled} answers — they sit under the topic itself.`
          : "This topic has no subtopics on the course."}
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted, mb: 0.4 }}>
        {d.covered} of {d.total} subtopics practised
        {d.lessonsDone != null ? ` · ${d.lessonsDone} lessons done` : ""}
        {d.unfiled ? ` · ${d.unfiled} answers with no subtopic recorded` : ""}
      </Typography>
      {list.map((st) => (
        <Box key={st.microTopic} sx={{
          display: "grid", gridTemplateColumns: "1fr 120px 92px", alignItems: "center", columnGap: 1,
          py: 0.35, opacity: st.done || st.lesson ? 1 : 0.55,
        }}>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "11.5px", color: st.done ? textPrimary : textSecondary }}>
            {humaniseTopic(st.microTopic)}
          </Typography>
          {/* The lesson (Learn) result for this subtopic, beside the practice one. */}
          <Typography sx={{ fontFamily: font, fontSize: "11px", textAlign: "right", fontVariantNumeric: "tabular-nums",
            color: !st.lesson ? textMuted : (st.lesson.total && st.lesson.score / st.lesson.total >= 0.75) ? success : warning }}
            title={st.lesson?.at ? `Lesson finished ${new Date(st.lesson.at).toLocaleDateString()}` : "Lesson not done"}>
            {st.lesson ? `lesson ✓ ${st.lesson.score}/${st.lesson.total}` : "no lesson"}
          </Typography>
          <Typography sx={{
            fontFamily: font, fontSize: "11px", textAlign: "right", fontVariantNumeric: "tabular-nums",
            color: st.percent == null ? textMuted : st.percent >= 75 ? success : warning,
          }}>
            {st.done ? `${st.right}/${st.done}${st.percent != null ? ` · ${st.percent}%` : ""}` : "not seen"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * One topic: its progress at rest, its subtopics and questions on demand, and
 * the two things you would do about it.
 *
 * Every topic on the paper is here, including the ones never opened — a bar at
 * zero is a fact worth having, and it is where the next piece of work comes
 * from.
 */
function TopicDetail({ topic, subject, subjectLabel, studentId, cache, minForTopicPercent, passMark, onSetWork, onMessage }) {
  const [open, setOpen] = useState(false);
  const wrong = topic.wrong == null ? topic.done - topic.right : topic.wrong;
  const showPercent = topic.done >= minForTopicPercent && topic.percent != null;
  const isMixed = topic.topic === "all";
  const started = topic.done > 0;

  return (
    <Box sx={{ borderTop: `1px solid ${border}` }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 74px", sm: "1fr 96px 84px 72px" },
          alignItems: "center", columnGap: 1.25, py: 0.8, px: 0.25, cursor: "pointer",
          "&:hover": { backgroundColor: surfaceRaised },
          "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: -2, borderRadius: "4px" },
        }}
      >
        <Typography noWrap sx={{
          fontFamily: font, fontSize: "12.5px",
          fontWeight: started ? 600 : 400,
          color: isMixed ? textSecondary : started ? textPrimary : textMuted,
          fontStyle: isMixed ? "italic" : "normal",
        }}>
          {humaniseTopic(topic.topic)}
        </Typography>

        {/* A bar per topic, so "how are they doing across this paper" is one
            downward glance rather than a column of numbers to compare. */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Box sx={{ height: 4, borderRadius: 2, backgroundColor: surfaceRaised, overflow: "hidden" }}>
            <Box sx={{
              width: started ? `${Math.max(4, Math.min(100, topic.percent ?? 0))}%` : "0%",
              height: "100%",
              backgroundColor: !started ? "transparent"
                : topic.percent >= passMark ? success : warning,
            }} />
          </Box>
        </Box>

        <Typography sx={{
          fontFamily: font, fontSize: "11.5px", color: started ? textSecondary : textMuted,
          textAlign: "right", fontVariantNumeric: "tabular-nums",
        }}>
          {started ? `${topic.done} answered` : "not seen"}
        </Typography>

        <Typography sx={{
          fontFamily: font, fontSize: "11.5px", textAlign: "right",
          color: wrong ? warning : started ? success : textMuted,
          fontWeight: wrong ? 700 : 400,
        }}>
          {started ? (wrong ? `${wrong} wrong` : "all right") : (topic.microTotal ? `${topic.microTotal} subs` : "—")}
        </Typography>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pb: 1.25, pl: { xs: 0.5, sm: 1.25 }, pr: 0.25 }}>
          <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, mb: 0.5 }}>
            {started
              ? `${topic.done} answered · ${topic.right} right${showPercent ? ` · ${topic.percent}%` : " · too few to score"}`
                + (topic.lastSeen ? ` · last seen ${ago(topic.lastSeen)}` : "")
              : "Never answered a question on this."}
          </Typography>

          {/* THE TWO THINGS YOU DO ABOUT A TOPIC, WHERE YOU LEARNED YOU NEEDED
              TO. Reading that a topic is weak and then hunting for the button
              is the step that stops people acting on what they just read. */}
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
            <Box
              onClick={(e) => { e.stopPropagation(); onSetWork?.({ subject, topic: topic.topic, subjectLabel }); }}
              sx={actionSx(true)}
            >
              Set work on this
            </Box>
            {started && !isMixed && (
              <Box
                onClick={(e) => { e.stopPropagation(); onMessage?.({ ...topic, subject, topic: topic.topic, subjectLabel }); }}
                sx={actionSx(false)}
              >
                Message about it
              </Box>
            )}
          </Box>

          {!isMixed && <Subtopics studentId={studentId} subject={subject} topic={topic.topic} cache={cache} />}

          {started && (
            <Questions
              studentId={studentId}
              subject={subject}
              topic={topic.topic}
              wrongCount={wrong}
              seed={topic.sample}
              cache={cache}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function SubjectCard({ subject, studentId, isOpen, onToggle, cache, minForTopicPercent, onSetWork, onMessage }) {
  const started = subject.done > 0;
  const topics = subject.topics || [];
  const realTopics = topics.filter((t) => t.topic && t.topic !== "all");
  const mixed = topics.find((t) => t.topic === "all") || null;
  const showPercent = subject.percent != null && subject.done >= (subject.shortBy == null ? 0 : Infinity);
  const lastWorked = ago(subject.lastSeen);

  // EVERY PAPER IS THE SAME CONTAINER, WORKED OR NOT.
  //
  // An untouched paper used to be a dashed one-liner, which made the list two
  // kinds of object with loose text between them. A student's ninth paper is
  // no less part of their course than their first — it just has nothing in it
  // yet, and the card says so in the same words and the same place as every
  // other card.
  return (
    <Box sx={{
      border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff",
      mb: 1, overflow: "hidden", opacity: started ? 1 : 0.72,
    }}>
      <Box
        // Untouched papers open too. There is something behind them now: the
        // whole topic list, each row with the subtopics under it and a button
        // that sets work on it. That is exactly where the next piece of work
        // comes from, and it used to be the one card you could not open.
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
        sx={{
          p: 1.5, cursor: "pointer",
          "&:hover": { backgroundColor: "#FCFBFA" },
          "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: -2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography noWrap sx={{ fontFamily: font, fontSize: "14px", fontWeight: 700, color: textPrimary, minWidth: 0 }}>
            {subject.label}
          </Typography>
          {subject.caaPaper && (
            <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, flexShrink: 0 }}>
              {subject.caaPaper}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Rating subject={subject} />
        </Box>

        {/* The stat line: how much, how well, how long ago — in that order,
            because "how well" is meaningless without "how much". */}
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: started ? textSecondary : textMuted, fontVariantNumeric: "tabular-nums" }}>
          {started
            ? `${subject.done} answered · ${subject.right} right`
              + (showPercent || subject.ready !== null ? ` · ${subject.percent}%` : "")
              + (lastWorked ? ` · last worked ${lastWorked}` : "")
            : `Nothing answered yet · ${subject.topicsTotal || 0} topics in this paper`}
        </Typography>

        <CoverageBar
          covered={subject.topicsCovered || 0}
          total={subject.topicsTotal || 0}
          topics={topics}
          passMark={subject.passMark}
          mixedOnly={(subject.topicsCovered || 0) === 0 && subject.done > 0}
        />
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.25, borderTop: `1px solid ${border}`, backgroundColor: "#FCFBFA" }}>
          {/* WHAT OPENING IT IS FOR.
              A card that expands into an unlabelled list of names and numbers
              makes the reader work out what they are looking at. This says it:
              how many topics of the paper have anything behind them, and then
              a header over the columns underneath. */}
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, pt: 1.25, pb: 0.5 }}>
            <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: textSecondary }}>
              {started ? "Every topic in this paper" : "What this paper covers"}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted }}>
              {subject.topicsCovered || 0} seen · {Math.max(0, (subject.topicsTotal || 0) - (subject.topicsCovered || 0))} never opened
            </Typography>
          </Box>

          <Box sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr 74px", sm: "1fr 96px 84px 72px" },
            columnGap: 1.25, px: 0.25, pb: 0.4,
          }}>
            <Typography sx={{ fontFamily: font, fontSize: "10px", letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted }}>
              Topic
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10px", letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted, display: { xs: "none", sm: "block" } }}>
              Score
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10px", letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted, textAlign: "right" }}>
              Answered
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10px", letterSpacing: "0.05em", textTransform: "uppercase", color: textMuted, textAlign: "right" }}>
              Wrong
            </Typography>
          </Box>

          {realTopics.length === 0 && !mixed && (
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, py: 1.25 }}>
              Answered, but not filed under a topic.
            </Typography>
          )}

          {realTopics.map((t) => (
            <TopicDetail
              key={t.topic}
              topic={t}
              subject={subject.subject}
              subjectLabel={subject.label}
              passMark={subject.passMark}
              studentId={studentId}
              cache={cache}
              minForTopicPercent={minForTopicPercent}
              onSetWork={onSetWork}
              onMessage={onMessage}
            />
          ))}

          {/* Whole-subject practice last, and named for what it is. It is most
              of what a real student produces, so hiding it makes the subject's
              own totals stop adding up on screen. */}
          {mixed && (
            <TopicDetail
              topic={mixed}
              subject={subject.subject}
              subjectLabel={subject.label}
              passMark={subject.passMark}
              studentId={studentId}
              cache={cache}
              minForTopicPercent={minForTopicPercent}
              onSetWork={onSetWork}
              onMessage={onMessage}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function RecordSubjects({ coverage, lessons = [], studentId, onSetWork, onMessage }) {
  const [openSubject, setOpenSubject] = useState(null);
  const cache = useRef(new Map());

  /**
   * NEVER RENDER NOTHING. An older backend sends no `coverage`, and most
   * students have answered nothing, so the course's own subject list — always
   * sent as `lessons` — is the floor.
   */
  const subjects = coverage?.subjects?.length
    ? coverage.subjects
    : (lessons || []).map((l) => ({
      subject: l.subject || String(l.n),
      label: l.title,
      caaPaper: l.caaPaper || null,
      phase: l.phase ?? null,
      phaseLabel: null,
      done: 0, right: 0, percent: null, lastSeen: null,
      passMark: l.standard || 75, ready: null, shortBy: 20, faded: false,
      topics: [], topicsCovered: 0, topicsTotal: 0,
    }));
  if (!subjects.length) return null;

  const t = coverage?.totals || { done: 0, right: 0, subjectsTotal: subjects.length };
  const minForTopicPercent = coverage?.minForTopicPercent || 8;
  const pace = coverage?.pace || null;
  const untouched = subjects.filter((s) => !s.done).length;
  const topicsCovered = subjects.reduce((n, s) => n + (s.topicsCovered || 0), 0);
  const topicsTotal = subjects.reduce((n, s) => n + (s.topicsTotal || 0), 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 1.25, flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 800, color: textPrimary, letterSpacing: "-0.01em" }}>
          The course
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, fontVariantNumeric: "tabular-nums" }}>
          {t.done
            ? `${subjects.length - untouched} of ${subjects.length} papers started · ${topicsCovered} of ${topicsTotal} topics seen · ${t.done} answers${pace ? ` · ~${pace.medianSeconds}s each` : ""}`
            : `${subjects.length} papers · ${topicsTotal} topics · nothing answered yet`}
        </Typography>
      </Box>

      {/* One container per paper, and nothing between them. The phase
          headings that used to divide these — "Before your first solo",
          "Talking to people" — were loose text sitting outside the cards, and
          they split nine papers into four short lists that each read as an
          unfinished section. The order is still the course's own. */}
      {subjects.map((s) => (
        <SubjectCard
          key={s.subject}
          subject={s}
          studentId={studentId}
          cache={cache}
          minForTopicPercent={minForTopicPercent}
          onSetWork={onSetWork}
          onMessage={onMessage}
          isOpen={openSubject === s.subject}
          onToggle={() => setOpenSubject(openSubject === s.subject ? null : s.subject)}
        />
      ))}
    </Box>
  );
}
